import { Compiler, Injector, NgModule, SystemJsNgModuleLoader } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { RouterModule, Router } from "@angular/router";

import { TCITenantEnvTools, ITCITenantComponentOptions, TCITenantAppType } from "@tci/common";

import { NgAuthGuardService } from "../ng-shared/ng-services/ng-auth-guard.service";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { TCITenantRemoteServices } from "../ng-tenant/tci-tenant-remote-services";
import { TciTenantComponentCacheService } from "../ng-tenant/tci-tenant-component-cache.service";
import { TciTenantRouteDeActivator } from "../ng-tenant/tci-tenant-route-de-activator.service";
import { TciTenantEnvToolsRouteActivator } from "./ng-env-tools.route-activator";
import { TCIRouteSelectorResolver } from "../tci-tenant-apps-route-activator";
import { NgSharedModule } from "../ng-shared/ng-shared.module";
import { BaseHybridConnectionService } from "./base-hybrid-connection/base-hybrid-connection.sevice";

import { BaseTibcliDownloadService } from "./base-tibcli-download/base-tibcli-download.service";
import { BaseHybridAgentDownloadService } from "./base-hybrid-agent-download/base-hybrid-agent-download.service";
import { BaseUnifiedManagementDownloadService } from "./base-unified-management-download/base-unified-management-download.service";
import { BaseSupplementListService } from "./base-supplement/base-supplement-list.service";
import { NgEnvToolsComponent } from "./ng-env-tools.component";
import { NgEnvToolLoaderComponent, NgEnvToolsDynLoaderComponent, NgEnvToolsComponentInput } from "./tool-loader/ng-env-tools-loader.component";
import { NgWaitModule } from "../ng-shared/ng-components/ng-wait/ng-wait.module";
import { TciTenantModule } from "../ng-tenant/tci-tenant.module";
import { TCITenantMenuServices } from "../ng-tenant/tci-tenant.menu";
import { NgAppFeatureToggleService } from "../ng-shared/ng-services/ng-app-feature-toggle.service";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";

import { TranslateService } from "@ngx-translate/core";
import { DropdownModule } from "primeng/primeng";
import { Observable } from "rxjs";
import { BaseAgentListService } from "./base-agent/base-agent-list.service";

function TciEnvToolsTenantRouteActivatorFactory(
    tenantMenuSrv: TCITenantMenuServices,
    userService: NgUserService,
    featureToggleService: NgAppFeatureToggleService,
    authSvc: NgAuthGuardService
) {
    const routeResolver: TCIRouteSelectorResolver = input => TCITenantEnvTools
        .getAllItems()
        .find(item => item.getRouteId() === input);

    const inputResolver: any = (input, apptype, compOptions: ITCITenantComponentOptions<any>) =>
        Observable.of(
            new NgEnvToolsComponentInput(
                TCITenantAppType.createPseudoTenant(...TCITenantAppType.getAllTypes()),
                userService,
                featureToggleService,
                compOptions
            )
        );

    return new TciTenantEnvToolsRouteActivator(tenantMenuSrv, authSvc, routeResolver, inputResolver);
}
function TciEnvToolsTenantRouteDeActivatorFactory(
    parentInjector: Injector,
    loader: SystemJsNgModuleLoader,
    compiler: Compiler,
    tenantRemoteServices: TCITenantRemoteServices,
    tenantSvc: TciTenantService,
    tenantCacheService: TciTenantComponentCacheService,
    router: Router
) {
    return TciTenantRouteDeActivator.getInstance();
}

@NgModule({
    imports: [
        TciTenantModule,
        RouterModule.forChild([
            {
                path: "envtools",
                component: NgEnvToolsComponent,
                pathMatch: "full",
                canActivate: [
                    NgAuthGuardService
                ]
            },
            {
                path: "envtools/:routeid",
                component: NgEnvToolLoaderComponent,
                canActivate: [
                    // NgAuthGuardService, // IPAS-14535 - EnvTools guard already includes the AuthGuard
                    TciEnvToolsTenantRouteActivatorFactory
                ],
                canDeactivate: [
                    TciTenantRouteDeActivator
                ]
            },
            {
                path: "envtools/**",
                redirectTo: "envtools"
            }
        ]),
        CommonModule,
        DropdownModule,
        FormsModule,
        NgWaitModule,
        NgSharedModule.forRoot()
    ],
    declarations: [
        NgEnvToolsComponent,
        NgEnvToolsDynLoaderComponent,
        NgEnvToolLoaderComponent
    ],
    providers: [
        {
            provide: TciEnvToolsTenantRouteActivatorFactory,
            useFactory: TciEnvToolsTenantRouteActivatorFactory,
            deps: [
                TCITenantMenuServices,
                NgUserService,
                NgAppFeatureToggleService,
                NgAuthGuardService
            ]
        },
        {
            provide: TciTenantRouteDeActivator,
            useFactory: TciEnvToolsTenantRouteDeActivatorFactory,
            deps: [
                Injector,
                SystemJsNgModuleLoader,
                Compiler,
                TCITenantRemoteServices,
                TciTenantService,
                TciTenantComponentCacheService,
                Router
            ]
        },
        // {
        //     provide: NgTenantGuard,
        //     useFactory: NgTenantGuard,
        //     deps: [Router, TCITenantMenuServices]
        // },
        BaseTibcliDownloadService,
        BaseHybridAgentDownloadService,
        BaseUnifiedManagementDownloadService,
        BaseAgentListService,
        BaseHybridConnectionService,
        BaseSupplementListService
    ]
})
export class NgEnvToolsModule {
    constructor(private translate: TranslateService) {
        // this language will be used as a fallback when a translation isn't found in the current language
        this.translate.setDefaultLang("en");

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        this.translate.use("en");
    }
}
